/**
 * The nine tabs of a programpost, as data (PERFORMANCE.md, «Faner»).
 *
 * ---------------------------------------------------------------------------------------------
 * THE ORDER IS THE SPECIFICATION, IN ITS OWN WORDS: «Rekkefølgen betyr noe og skal beholdes.»
 *
 * It reads as a working day rather than as a menu — who is coming, what has to be done, how they
 * get here, where they sleep, what we owe them, what it costs, who else may see it. So the list is
 * written once here and the page renders it, rather than eight JSX blocks whose order is whatever
 * somebody last edited.
 *
 * ---------------------------------------------------------------------------------------------
 * `permission` IS THE CELL THAT OPENS THE TAB, AND ONLY KONTRAKT HAS ONE.
 *
 * Seven tabs are about the booking and open with the booking — the page's own `performance:read`
 * has already been asked by the route. Kontrakt is different: «Hele fanen krever egen rettighet og
 * deles aldri med eksterne», so it names `contract:read` and the tab bar hides it from anybody
 * without it. Decision 0005's rule about menu entries applied to a tab, which is what C-44 did for
 * a panel.
 *
 * A HIDDEN TAB IS NOT A CLOSED DOOR. The API refuses again on every request, and a reader who
 * types the search parameter lands on the first tab rather than on an empty frame — see
 * `resolveTab`.
 */
import type { Action } from '@tvitvi/shared';

import type { TKey } from '@/i18n/types';

export const PERFORMANCE_TAB_IDS = [
  'overview',
  'people',
  'tasks',
  'travel',
  'stay',
  'advance',
  'contract',
  /*
   * DELING ER IKKE LENGER EN FANE. Atle, 2. september: «Deling legges under people, slik at man der
   * legger folk til og velger om de skal ha tilgang, og hva de skal ha tilgang til.»
   *
   * Id-en står igjen fordi den er i URL-er folk har åpne og i bokmerker, og en ukjent fane er en
   * tom side. `PERFORMANCE_TABS` lister den ikke lenger, så den tegnes ikke — og
   * `PerformanceDetailPage` sender den videre til People.
   */
  'sharing',
  /*
   * «Legg til en egen side helt til høyre i stien» — Atle, 2. september, and «helt til høyre» is
   * the whole placement instruction. It goes after Deling rather than beside the operational tabs
   * because it is not a step of the working day the order above describes: it is where the people
   * doing those steps talk to each other, and it belongs at the end of the row the way a chat
   * panel belongs at the edge of a screen.
   */
  'chat',
] as const;

export type PerformanceTabId = (typeof PERFORMANCE_TAB_IDS)[number];

export interface PerformanceTab {
  id: PerformanceTabId;
  labelKey: TKey;
  /** The cell that opens it, when it has one of its own. See the header. */
  permission?: Action;
  /**
   * Whether the tab has a real panel behind it yet.
   *
   * A FLAG RATHER THAN A SHORTER LIST, because the ORDER is the specification and must not be
   * edited as tabs arrive: «Rekkefølgen betyr noe og skal beholdes.» An unbuilt tab is still
   * drawn — it says what will live there, which is what lets Atle correct it — but it is not
   * where the page LANDS. `defaultTab` picks the first built one, so nobody opening a booking
   * meets a «kommer» card, and the day Oversikt is built the default moves back to the front of
   * the list on its own.
   */
  built?: boolean;
}

export const PERFORMANCE_TABS: readonly PerformanceTab[] = [
  { id: 'overview', labelKey: 'performances.tab.overview', built: true },
  { id: 'people', labelKey: 'performances.tab.people', built: true },
  { id: 'tasks', labelKey: 'performances.tab.tasks', built: true },
  { id: 'travel', labelKey: 'performances.tab.travel', built: true },
  { id: 'stay', labelKey: 'performances.tab.stay', built: true },
  { id: 'advance', labelKey: 'performances.tab.advance', built: true },
  {
    id: 'contract',
    labelKey: 'performances.tab.contract',
    built: true,
    /*
     * The ONLY tab with a permission of its own, and the spec is why: «hele fanen krever egen
     * rettighet og deles aldri med eksterne». Every other tab borrows the performance cells;
     * a fee and a cancellation clause are not the same question as a stage time.
     */
    permission: 'contract:read',
  },
  /*
   * NO `permission` OF ITS OWN, deliberately. «En chat for alle som er involvert kan være med i» —
   * and «involvert» is already answered by the cell that opens the booking, so the chat borrows
   * `performance:read` like six of the seven tabs above it. Giving it a cell of its own would mean
   * a switch in Settings that could hide the conversation from somebody who can read everything
   * else about the act, which is a stranger state than either answer.
   */
  /*
   * Its label sits under `chat.*` rather than beside the other eight under `performances.tab.*`.
   * That is not tidiness: the locale files are shared and append-only across parallel agents, and
   * a feature that keeps every key it owns in one branch is a feature that can be added, moved or
   * removed without touching anybody else's block.
   */
  { id: 'chat', labelKey: 'chat.tab', built: true },
];

/**
 * Which tab a URL asks for, narrowed to one this reader may actually open.
 *
 * TWO WAYS TO FALL BACK, AND BOTH LAND ON THE FIRST TAB. An unknown value is somebody editing the
 * address bar or following a link from a version of the page that had a tab this one does not.
 * A KNOWN value the reader lacks the permission for is the interesting case: without this, typing
 * `?tab=contract` would render an empty frame under a tab bar that does not show the tab — which
 * looks like the page is broken rather than like a refusal.
 */
export function resolveTab(
  raw: string | undefined,
  allowed: (tab: PerformanceTab) => boolean,
): PerformanceTabId {
  /*
   * DELING FLYTTET INN I PEOPLE (Atle, 2. september), og en gammel lenke skal lande DER framfor
   * på Oversikt. Uten denne linja faller `?tab=sharing` gjennom til `defaultTab`, som er riktig
   * i den forstand at siden ikke er tom og feil i den ene som betyr noe: den som fulgte lenken
   * ville sett en side uten det de kom for.
   */
  const asked = raw === 'sharing' ? 'people' : raw;

  const wanted = PERFORMANCE_TABS.find((tab) => tab.id === asked);
  if (wanted === undefined || !allowed(wanted)) return defaultTab(allowed);
  return wanted.id;
}

/**
 * Where a booking opens when the URL says nothing.
 *
 * THE FIRST TAB THAT IS BOTH BUILT AND ALLOWED, not simply the first tab. Landing on a card that
 * says «kommer» is a worse first impression than landing one tab along, and the flag means the
 * default corrects itself as tabs arrive rather than being a constant somebody has to remember to
 * move.
 *
 * The last fallback is `overview`, which cannot be reached today — every reader may open People —
 * and exists so this function is total rather than partial.
 */
export function defaultTab(allowed: (tab: PerformanceTab) => boolean): PerformanceTabId {
  return (
    PERFORMANCE_TABS.find((tab) => tab.built === true && allowed(tab))?.id ??
    PERFORMANCE_TABS.find(allowed)?.id ??
    'overview'
  );
}
